import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getSwitchConfirmations } from '../../api/documents';

type SwitchConfirmation = string[];

interface SwitchConfirmationsState {
  switchConfirmations: SwitchConfirmation;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const initialState: SwitchConfirmationsState = {
  switchConfirmations: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const fetchSwitchConfirmations = createAsyncThunk<
  SwitchConfirmation,
  number,
  { state: { switchConfirmations: SwitchConfirmationsState }; rejectValue: string }
>('switchConfirmations/fetchSwitchConfirmations', async (company_id, { getState, rejectWithValue }) => {
  const { switchConfirmations, lastFetched } = getState().switchConfirmations;

  if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
    return switchConfirmations;
  }

  try {
    const switchConfirmationsData: SwitchConfirmation = await getSwitchConfirmations(company_id);
    return switchConfirmationsData;
  } catch (err) {
    if (err instanceof Error) {
      console.error('Error fetching switch confirmations:', err.message);
      return rejectWithValue(err.message);
    } else {
      console.error('Unknown error fetching switch confirmations');
      return rejectWithValue('An unknown error occurred');
    }
  }
});

const switchConfirmationsSlice = createSlice({
  name: 'switchConfirmations',
  initialState,
  reducers: {
    resetSwitchConfirmationsState: (state) => {
      state.switchConfirmations = [];
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSwitchConfirmations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSwitchConfirmations.fulfilled, (state, action: PayloadAction<SwitchConfirmation>) => {
        state.loading = false;
        state.switchConfirmations = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchSwitchConfirmations.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'An unknown error occurred';
        console.error('Error in state:', state.error);
      });
  },
});

export const { resetSwitchConfirmationsState } = switchConfirmationsSlice.actions;
export default switchConfirmationsSlice.reducer;
